<script setup lang="ts">
import { computed } from 'vue'
import DJIcon from '~/components/ui/icon/Icon.vue'
import DJButton from '~/components/ui/button/Button.vue'
import { IconExport, IconDownloadSimple, IconPanorama, IconCalendarDots, IconStarFill, IconCaretLeft } from '~/components/ui/icon/icons'
import { DESKTOP } from '~/constant/common'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

interface Props {
  breadcrumbs: Array<{ link: string; title: string }>
  imagesLength: number
  countTraveller: number
  countReview: number
  nameDesigner: string
  rating: number | string
}
const props = withDefaults(defineProps<Props>(), {
  breadcrumbs: () => [],
  imagesLength: 0,
  countTraveller: 0,
  countReview: 1,
  nameDesigner: '',
  rating: '4.5',
})
const emits = defineEmits(['share', 'download', 'view-all-images'])
const INDEX_DES_TRIP = 0
const INDEX_TRIP_NAME = 1
const DEFAULT_REVIEW = 1
const className = {
  breadcrumbTitle: 'text-md-md white',
}
const generalStore = useGeneralInfoStore()

const windowWidth = computed(() => generalStore.generalData.windowWidth)
const isDesktop = computed(() => windowWidth.value >= DESKTOP)
const itemsAction = computed(() => {
  const result = [
    {
      icon: IconExport,
      label: 'Share',
      action: () => emits('share'),
    },
    {
      icon: IconDownloadSimple,
      label: 'Download Itinerary',
      action: () => emits('download'),
    },
    {
      icon: IconPanorama,
      label: `View all images (${props.imagesLength})`,
      action: () => emits('view-all-images'),
    },
  ]
  return isDesktop.value ? result : result.slice(0, -1)
})
const breadcrumbsTop = computed(() => {
  const result = [{ link: '/', title: 'Home' }]
  if (props.breadcrumbs.length) {
    result.push(props.breadcrumbs[INDEX_DES_TRIP])
  }
  return result
})
const destinationRouteTrip = computed(() => {
  return props.breadcrumbs[INDEX_DES_TRIP]
})
const tripName = computed(() => {
  if (props.breadcrumbs.length) {
    return props.breadcrumbs[INDEX_TRIP_NAME].title || ''
  }
  return ''
})
const subDescription = computed(() => {
  const { countTraveller, countReview, nameDesigner } = props
  const review = countReview ? countReview : DEFAULT_REVIEW
  return countTraveller
    ? `${countTraveller} ${countTraveller === 1 ? 'traveller' : 'travellers'} have booked with ${nameDesigner}`
    : `(${review} ${review === 1 ? 'review' : 'reviews'})`
})
</script>

<template>
  <div class="z-2 py-24 px-32 pb-40 mb-px-20 mb-pb-40" :style="isDesktop ? 'width: calc(1220px + 64px)' : 'width: 100%'">
    <div class="maxw-800">
      <ul v-if="isDesktop" class="ttc">
        <li class="dib" v-for="(item, index) in breadcrumbsTop" :key="`${index}`">
          <template v-if="item.link">
            <router-link :to="{ path: item.link }">
              <span :class="className.breadcrumbTitle">{{ item.title }}</span>
            </router-link>
          </template>
          <template v-else>
            <span :class="className.breadcrumbTitle">{{ item.title }}</span>
          </template>
          <template v-if="index < breadcrumbsTop.length - 1">
            <span class="ml-4 mr-4" :class="className.breadcrumbTitle">/</span>
          </template>
        </li>
      </ul>
      <h1 class="white heading-3 mt-12 mb-8">{{ tripName }}</h1>
      <div class="flex white mb-flex-column">
        <div class="flex items-center">
          <DJIcon :size="16" color="white" class="mr-8">
            <IconCalendarDots />
          </DJIcon>
          <span class="text-md-md mb-text-sm-rg">Customisable Itinerary</span>
        </div>
        <div class="flex items-center ml-16 mb-ml-0 mb-mt-4">
          <DJIcon :size="16" color="#F79009" class="mr-4">
            <IconStarFill />
          </DJIcon>
          <span class="text-md-md mb-text-sm-rg">
            <span class="fw-600">{{ rating }}</span> •
            {{ subDescription }}
          </span>
        </div>
      </div>
      <div v-if="!isDesktop" class="flex justify-between absolute top-16 mb-left-16 mb-right-16 left-32 right-32">
        <router-link :to="{ path: destinationRouteTrip.link }">
          <DJButton :prefix-icon="IconCaretLeft" rounded style="background-color: unset; padding-left: 0">
            <span class="maxw-116 overflow-hidden ellipsis white-space mb-text-md-rg">{{ `${destinationRouteTrip.title}` }}</span>
          </DJButton>
        </router-link>
        <DJButton :prefix-icon="IconPanorama" type="glass" rounded @click.prevent="emits('view-all-images')">
          <span class="mb-text-md-rg">{{ `View all (${imagesLength})` }}</span>
        </DJButton>
      </div>

      <div class="flex mt-12">
        <template v-if="isDesktop">
          <DJButton v-for="(item, index) in itemsAction" :key="index" :prefix-icon="item.icon" class="mr-8" type="glass" rounded @click.prevent="item.action">
            <span class="text-md-sb">{{ item.label }}</span>
          </DJButton>
        </template>
        <template v-else>
          <DJButton v-for="(item, index) in itemsAction" :key="index" :prefix-icon="item.icon" class="mr-8" type="glass" rounded @click.prevent="item.action">
            <span class="mb-text-md-rg">{{ item.label }}</span>
          </DJButton>
        </template>
      </div>
    </div>
  </div>
</template>
